import {
  ApolloCache,
  ApolloClient,
  ApolloClientOptions,
  ApolloLink,
  NormalizedCacheObject
} from '@apollo/client'
import { IncomingMessage, ServerResponse } from 'http'
import { TFunction } from '../../translations/useTranslations'
import { isDevelopment } from '../../utils/environment'
import { GraphqlApi } from './constants'
import { createErrorLink } from './errorLink'
import {
  cleanTypeNameLink,
  createAuthLink,
  createSplitLink,
  httpLink
} from './links'

export type ResolverContext = {
  req?: IncomingMessage
  res?: ServerResponse
}

export type AuthConfig = {
  useAuth?: boolean
  getHavelockAccessToken?: Function
  getProdactiveAccessToken?: Function
  onLogout?: (graphqlApi: GraphqlApi) => void
}

export type ApolloConfig = {
  cache: ApolloCache<NormalizedCacheObject>
  authConfig: AuthConfig
  defaultApiContext?: GraphqlApi
  resolvers?: ApolloClientOptions<ApolloConfig['cache']>['resolvers']
  triggerReconnection?: Function
  showErrorToast?: (message: string) => void
  translate?: TFunction
  getBusinessFailureMessage?: (typename: string) => string | undefined
}

const isSS = typeof window === 'undefined'
let apolloClient: ApolloClient<NormalizedCacheObject> | undefined

function createLink(config: ApolloConfig) {
  const authLink = createAuthLink(config)

  return ApolloLink.from([
    cleanTypeNameLink,
    authLink,
    createErrorLink({
      showErrorToast: config?.showErrorToast,
      onLogout: config?.authConfig?.onLogout,
      translate: config?.translate,
      getBusinessFailureMessage: config?.getBusinessFailureMessage
    }),
    isSS ? httpLink : createSplitLink(config)
  ])
}

function createApolloClient(config: ApolloConfig) {
  return new ApolloClient({
    link: createLink(config),
    cache: config.cache,
    devtools: {
      enabled: isDevelopment()
    },
    resolvers: config.resolvers
  })
}

export function initializeApollo(
  initialState: any = null,
  config: ApolloConfig
) {
  let _apolloClient

  if (!apolloClient) {
    _apolloClient = createApolloClient(config)
  } else {
    _apolloClient = apolloClient
  }

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  if (initialState) {
    _apolloClient.cache.restore(initialState)
  }
  // For SSG and SSR always create a new Apollo Client
  if (isSS) return _apolloClient
  // Create the Apollo Client once in the client
  if (!apolloClient) {
    apolloClient = _apolloClient
  }

  return _apolloClient
}
